// Copyright (c) 2021 WiseTime. All rights reserved.

import {
  TimeGroup,
  TimelineMarker,
  TimeRow,
  TrashStatus,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { getActivityHourLocal, getLocalTimezoneOffset } from "./dateTime"
import { ToolbarState } from "features/timeline/toolbar/slice"
import { TimeBlocksSortOrder, TimelineRowSortType } from "generated/timeline/toolbar/toolbar_pb"
import { getSortTypeStrategy, sortTagsListAlphabetically } from "./sorting"
import { PostErrorMessages } from "./slices/timeline"
import { SiteId } from "features/user-context/slice"
import { isMatchingShowSelectedItemToggle, isMatchingToolbarAndSearchFilters } from "./filters"
import { Site } from "../../generated/legebill/site/v4/site_pb"
import { notUndefined } from "../../lib/utils"
import { isSearchNotEmpty } from "./search-bar/searchUtils"
import { t } from "@lingui/macro"
import { DateTime } from "luxon"
import { computeDurationStep, secondsToHoursRounded } from "./timeGroups"
import { RoundingPreference } from "../../generated/wisetime/type/site_preferences_pb"

export type TimelineRow = (TimeRow.AsObject | TimeMarkerRow)
export const isTimeRow = (timelineRow: unknown): timelineRow is TimeRow.AsObject =>
  // Unlike time rows, time groups and time markers do not have timeGroupId property.
  (timelineRow as TimeRow.AsObject).timeGroupId !== undefined

export type TimeBlock = {
  timelineRows: TimelineRow[],
  // User local time yyyyMMddHH
  activityHour: number,
  activityHourLocalCurrentTz: number,
  activityHourLocalRecordedTz: number,
  // User's current time zone offset from UTC in hours
  timeZoneOffsetCurrent: number,
  // Recorded time zone offset from UTC in hours
  timeZoneOffsetRecorded: number,
  timeZoneRecorded: string | undefined,
}

const filterRowsForBlockAggKey = (
  source: TimeRow.AsObject[],
  aggKey: ActivityHourWithOffsetCompoundKey
): TimeRow.AsObject[] => {
  // Avoid duplicate time rows
  const seenTimeRowIds: { [id: string]: boolean } = {}
  return source
    .filter(timeRow => activityHourWithOffsetCompoundKeyForRow(timeRow) === aggKey)
    .filter(timeRow => seenTimeRowIds.hasOwnProperty(timeRow.id) ? false : (seenTimeRowIds[timeRow.id] = true))
}

// Representation of a time marker as a row in the timeline UI.
// It has fields that are consistent with TimeRow so that the 2 types can be mixed and sorted.
export type TimeMarkerRow = {
  activityHour: number,
  firstObservedInHour: number,
  timeZone: string,
} & TimelineMarker.AsObject

export const makeTimeMarkerRow = (timeMarker: TimelineMarker.AsObject, timeZoneName: string): TimeMarkerRow => {
  const date = DateTime.fromSeconds(timeMarker.startTime?.seconds ?? 0)
  const dateInUtc = date.toUTC()
  const activityHour = Number(dateInUtc.toFormat("yyyyMMddHH"))
  return ({
    ...timeMarker,
    activityHour,
    firstObservedInHour: dateInUtc.get("minute"),
    timeZone: timeZoneName,
    timeZoneOffset: getLocalTimezoneOffset(activityHour, timeZoneName),
  })
}
export const aggregateTimeBlocks = (
  timeRowsForDate: TimeRow.AsObject[],
  timeMarkersForDate: TimeMarkerRow[],
  timeZoneName: string,
): TimeBlock[] => {
  // Avoid duplicate time blocks
  const seenTimeBlockIds: { [id: string]: boolean } = {}
  const createTimeBlock = (
    {
      activityHour,
      timeZone,
      timeZoneOffset,
    }: TimelineRow,
    timeZoneNameFromState: string,
    timelineRows: TimelineRow[],
  ): TimeBlock => {
    const localTimezoneOffset = getLocalTimezoneOffset(activityHour, timeZoneNameFromState)
    const activityHourLocalCurrentTz = getActivityHourLocal(activityHour, localTimezoneOffset)
    return ({
      timelineRows,
      // yyyyMMddHH in UTC
      activityHour,
      // Local time yyyyMMddHH in user's current time zone
      activityHourLocalCurrentTz,
      // User local time yyyyMMddHH in the recorded time zone
      activityHourLocalRecordedTz: getActivityHourLocal(activityHour, timeZoneOffset),
      // User's current time zone offset from UTC in hours
      timeZoneOffsetCurrent: localTimezoneOffset,
      // Recorded time zone offset from UTC in hours
      timeZoneOffsetRecorded: timeZoneOffset,
      timeZoneRecorded: timeZone,
    })
  }
  const aggregatedTimeBlocks = timeRowsForDate
    .reduce((
      result: TimeBlock[],
      currTimeRow: TimeRow.AsObject,
      _index: number,
      source: TimeRow.AsObject[],
    ): TimeBlock[] => {
      const timeRowBlockAggKey = activityHourWithOffsetCompoundKeyForRow(currTimeRow)
      if (!result.some(timeBlock => activityHourWithOffsetCompoundKeyForBlock(timeBlock) === timeRowBlockAggKey)) {
        const rowsActivityHourLocalized = filterRowsForBlockAggKey(source, timeRowBlockAggKey)
          .map((timeRow: TimeRow.AsObject) => ({
            ...timeRow,
            tagsList: [...timeRow.tagsList].sort(sortTagsListAlphabetically),
          }))
        return [
          ...result,
          createTimeBlock(currTimeRow, timeZoneName, rowsActivityHourLocalized),
        ]
      }
      return result
    }, [])

  timeMarkersForDate.forEach((marker: TimeMarkerRow): void => {
    const blockIndexes = aggregatedTimeBlocks
      .map((timeBlock, index) => timeBlock.activityHour === marker.activityHour ? index : undefined)
      .filter(notUndefined)
    if (blockIndexes.length !== 0) {
      blockIndexes.forEach(index => {
        const block = aggregatedTimeBlocks[index]
        if (block) {
          const [row] = block.timelineRows
          // time marker should have time rows time zone info for correct sorting
          block.timelineRows.push(row ? ({
            ...marker,
            timeZoneOffset: row.timeZoneOffset,
            timeZone: row.timeZone,
          }) : marker)
        }
      })
    } else {
      aggregatedTimeBlocks.push(createTimeBlock(marker, timeZoneName, [marker]))
    }
  })

  return aggregatedTimeBlocks
    .filter(timeBlock => {
      const timeBlockId = activityHourWithOffsetCompoundKeyForBlock(timeBlock)
      return seenTimeBlockIds.hasOwnProperty(timeBlockId) ? false : (seenTimeBlockIds[timeBlockId] = true)
    })
    .map((timeBlock: TimeBlock): TimeBlock => ({
      ...timeBlock,
      timelineRows: [...timeBlock.timelineRows]
        .sort(getSortTypeStrategy(TimeBlocksSortOrder.ASCENDING, TimelineRowSortType.CHRONOLOGY))
    }))
    .sort(chronologicalWithTimeZone())
}

const chronologicalWithTimeZone = () => {
  let prevTimezone: string | undefined
  return (timeBlock1: TimeBlock, timeBlock2: TimeBlock) => {
    // sort by activity hour
    const timeZoneOffsetForBlock2 = timeBlock2.timeZoneOffsetRecorded.toString()
    if (timeBlock1.activityHourLocalCurrentTz > timeBlock2.activityHourLocalCurrentTz) {
      prevTimezone = timeZoneOffsetForBlock2
      return 1
    }
    const timeZoneOffsetForBlock1 = timeBlock1.timeZoneOffsetRecorded.toString()
    if (timeBlock1.activityHourLocalCurrentTz < timeBlock2.activityHourLocalCurrentTz) {
      prevTimezone = timeZoneOffsetForBlock1
      return -1
    }
    // if activity hours are the same, sort blocks with the respect of previous block timezone
    // so time blocks with the same time zone go side by side
    if (prevTimezone === undefined || timeZoneOffsetForBlock1 === prevTimezone) {
      prevTimezone = timeZoneOffsetForBlock1
      return -1
    }
    prevTimezone = timeZoneOffsetForBlock2
    return 1
  }
}

type ActivityHourWithOffsetCompoundKey = string
export const activityHourWithOffsetCompoundKeyForBlock = (timeBlock: TimeBlock): ActivityHourWithOffsetCompoundKey =>
  buildActivityHourCompoundKey(timeBlock.activityHourLocalRecordedTz, timeBlock.timeZoneOffsetRecorded)

const activityHourWithOffsetCompoundKeyForRow = (timeRow: TimeRow.AsObject): ActivityHourWithOffsetCompoundKey =>
  buildActivityHourCompoundKey(
    getActivityHourLocal(timeRow.activityHour, timeRow.timeZoneOffset),
    timeRow.timeZoneOffset,
  )

const buildActivityHourCompoundKey = (activityHourLocalCurrentTz: number, timezoneOffset: number): ActivityHourWithOffsetCompoundKey =>
  activityHourLocalCurrentTz.toString() + "_" + timezoneOffset.toString()

export const filterTimeBlocks = (timeBlocks: TimeBlock[], activeTimeRowIds: string[], filter?: TimeBlocksFilter): TimeBlock[] => {
  const timeBlocksWithNonTrashed = timeBlocks.map(timeBlock => {
    const filteredRows = timeBlock.timelineRows.filter((timelineRow: TimelineRow) => {
      if (isTimeRow(timelineRow)) {
        return timelineRow.trashStatus !== TrashStatus.TRASHED
      }
      return true
    })
    return ({
      ...timeBlock,
      timelineRows: filteredRows,
    })
  }).filter(timeBlock => timeBlock.timelineRows.length > 0)
  if (!filter) {
    return timeBlocksWithNonTrashed
  }

  const toolbar = filter.toolbarState
  const updatedTimeBlocks = timeBlocksWithNonTrashed.map(timeBlock => {
    if (toolbar.viewFlags.length > 0 || isSearchNotEmpty(toolbar.searchTerms) || filter.showSelectedTimeRows) {
      const filteredRows = timeBlock.timelineRows.filter((timelineRow: TimelineRow) => {
        if (isTimeRow(timelineRow)) {
          return isMatchingToolbarAndSearchFilters(timelineRow, toolbar, filter.currentSiteId)
            && isMatchingShowSelectedItemToggle(timelineRow, filter.showSelectedTimeRows, activeTimeRowIds)
        }
        // Exclude timeline markers when showSelectedTimeRows is true
        return !filter.showSelectedTimeRows
      })
      return ({
        ...timeBlock,
        timelineRows: filteredRows
      })
    }
    return timeBlock
  }).filter(timeBlock => timeBlock.timelineRows.length > 0)
    .map(timeBlock => {
      if (toolbar.sortType !== TimelineRowSortType.CHRONOLOGY) {
        return ({
          ...timeBlock,
          timelineRows: [...timeBlock.timelineRows].sort(getSortTypeStrategy(toolbar.timeBlocksSortOrder, toolbar.sortType))
        })
      }
      return timeBlock
    })

  if (toolbar.timeBlocksSortOrder === TimeBlocksSortOrder.DESCENDING) {
    return updatedTimeBlocks.reverse()
  }
  return updatedTimeBlocks
}

export type TimeBlocksFilter = {
  toolbarState: ToolbarState,
  showSelectedTimeRows: boolean,
  currentSiteId: SiteId | undefined,
}

export const getTimeBlocks = (
  timeRowsForDate: TimeRow.AsObject[],
  timeZoneName: string,
  filter: TimeBlocksFilter,
  timeMarkersForDate: TimelineMarker.AsObject[] | undefined = [],
  activeTimeRowIds: string[],
): TimeBlock[] => (
  filterTimeBlocks(
    aggregateTimeBlocks(
      timeRowsForDate,
      timeMarkersForDate.map(timeMarker => makeTimeMarkerRow(timeMarker, timeZoneName)),
      timeZoneName,
    ),
    activeTimeRowIds,
    filter,
  )
)

export const getTimeRowsDurationSecs = (timeRows: TimeRow.AsObject[]): number =>
  timeRows.reduce((result: number, timeRow: TimeRow.AsObject) => {
    if (timeRow.trashStatus !== TrashStatus.TRASHED) {
      return result + (timeRow.duration?.seconds ?? 0)
    }
    return result
  }, 0)

export const getTrashedTimeRowsDurationSecs = (timeRows: TimeRow.AsObject[]): number =>
  timeRows.reduce((result: number, timeRow: TimeRow.AsObject) => {
    if (timeRow.trashStatus === TrashStatus.TRASHED) {
      return result + (timeRow.duration?.seconds ?? 0)
    }
    return result
  }, 0)

export const getTimeBlockDurationSecs = (timeRows: TimeRow.AsObject[]): number =>
  Math.min(getTimeRowsDurationSecs(timeRows), 3600)

const isTagRequired = (currentSite: Site.AsObject) => currentSite.postTimeRequirements?.tagRequired ?? false
const isActivityTypeRequired = (currentSite: Site.AsObject) => currentSite.postTimeRequirements?.activityTypeRequired ?? false
// backend returns a default / "empty" instance of ActivityType when the site has no default set
const hasDefaultActivityType = (currentSite: Site.AsObject) => currentSite.defaultActivityType !== undefined &&
  currentSite.defaultActivityType.id !== ""

export const isTimeRequiringTag = (timeGroup: TimeGroup.AsObject, currentSite: Site.AsObject): boolean => {
  const hasTagFromCurrentSite = timeGroup.tagsList.some(tag => tag.siteId === currentSite.id)
  return isTagRequired(currentSite) && !hasTagFromCurrentSite
}

export const isTimeRequiringActivityType = (timeGroup: TimeGroup.AsObject, currentSite: Site.AsObject): boolean => {
  if (hasDefaultActivityType(currentSite)) {
    return false
  }
  const hasActivityTypeFromCurrentSite = timeGroup.activityTypesList
    .some(activityType => activityType.siteId === currentSite.id)
  return isActivityTypeRequired(currentSite) && !hasActivityTypeFromCurrentSite
}

export const isTimeRequiringResolveTagCategory = (timeGroup: TimeGroup.AsObject, currentSite: Site.AsObject): boolean => (
  timeGroup.tagCategoriesList.some(tagCategory => tagCategory.siteId === currentSite.id)
)

export const isGroupRequiringDuration = (timeGroup: TimeGroup.AsObject, roundingPreference: RoundingPreference): boolean =>
  secondsToHoursRounded(timeGroup.totalDuration?.seconds ?? 0, roundingPreference) < computeDurationStep(roundingPreference)

export type PostValidationResult = {
  errorMessages: PostErrorMessages,
  snackBarError: string | undefined,
  postableRowIds: string[],
}

type TimeValidationErrorKeys = "REQUIRING_TAG"
  | "REQUIRING_ACTIVITY_TYPE"
  | "REQUIRING_RESOLVE_TAG_CATEGORY"
  | "REQUIRING_DURATION"
export const timeValidationErrorMessages: Record<TimeValidationErrorKeys, string> = {
  REQUIRING_TAG: t({
    id: "msg_post_time_assign_a_tag",
    message: "Assign a tag",
  }),
  REQUIRING_ACTIVITY_TYPE: t({
    id: "msg_post_time_assign_an_activity_type",
    message: "Assign an activity type",
  }),
  REQUIRING_RESOLVE_TAG_CATEGORY: t({
    id: "msg_post_time_resolve_tag_category",
    message: "Choose a tag from the tag category or remove it",
  }),
  REQUIRING_DURATION: t({
    id: "msg_invalid_group_duration",
    message: "Group duration should be at least 0.1 hours",
  }),
}

export const listItemPrefix = "- "
export const errorMessageOfPostingTime = (
  timeGroup: TimeGroup.AsObject,
  currentSite: Site.AsObject,
): string | undefined => {
  const requiringTag = isTimeRequiringTag(timeGroup, currentSite)
  const requiringActivityType = isTimeRequiringActivityType(timeGroup, currentSite)
  const requiringResolveTagCategory = isTimeRequiringResolveTagCategory(timeGroup, currentSite)
  const requiringDuration = isGroupRequiringDuration(timeGroup, currentSite.siteRoundingPreference)
  const messageStart = t({ id: "msg_post_group_tip_error_message_start", message: "To post this group, you need to:" })
  if (requiringTag || requiringActivityType || requiringResolveTagCategory || requiringDuration) {
    return (
      `${messageStart}\n` +
      `${requiringTag ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TAG}\n` : ""}` +
      `${requiringActivityType ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_ACTIVITY_TYPE}\n` : ""}` +
      `${requiringResolveTagCategory ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_RESOLVE_TAG_CATEGORY}\n` : ""}` +
      `${requiringDuration ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_DURATION}\n` : ""}`
    )
  }
  return undefined
}

export const snackBarErrorOfPostingTime = (
  timeGroups: TimeGroup.AsObject[],
  currentSite: Site.AsObject,
): string | undefined => {
  const requiringTag = timeGroups.some(t => isTimeRequiringTag(t, currentSite))
  const requiringActivityType = timeGroups.some(t => isTimeRequiringActivityType(t, currentSite))
  const requiringResolveTagCategory = timeGroups.some(t => isTimeRequiringResolveTagCategory(t, currentSite))
  const requiringDuration = timeGroups.some(t => isGroupRequiringDuration(t, currentSite.siteRoundingPreference))
  const messageStart = t({
    id: "msg_post_group_snackbar_error_message_start",
    message: "Unable to post some of the groups. You may need to:",
  })
  if (requiringTag || requiringActivityType || requiringResolveTagCategory || requiringDuration) {
    return (
      `${messageStart}\n` +
      `${requiringTag ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TAG}\n` : ""}` +
      `${requiringActivityType ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_ACTIVITY_TYPE}\n` : ""}` +
      `${requiringResolveTagCategory ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_RESOLVE_TAG_CATEGORY}\n` : ""}` +
      `${requiringDuration ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_DURATION}\n` : ""}`
    )
  }
  return undefined
}

export const isValidationError = (errorMessage: string): boolean => (
  Object.values(timeValidationErrorMessages).some(message => errorMessage.includes(message))
)

export const validateTimeGroupsForPost = (
  currentSite: Site.AsObject,
  timeGroups: TimeGroup.AsObject[],
): PostValidationResult => {
  const errorMessages: PostErrorMessages = {}
  const postableRowIds: string[] = []

  timeGroups.forEach(timeGroup => {
    const errorMessage = errorMessageOfPostingTime(timeGroup, currentSite)

    if (errorMessage) {
      errorMessages[timeGroup.id] = errorMessage
    } else {
      postableRowIds.push(timeGroup.id)
    }
  })

  return ({
    errorMessages,
    snackBarError: snackBarErrorOfPostingTime(timeGroups, currentSite),
    postableRowIds,
  })
}

export const countUntrashedRows = (timeRows: TimeRow.AsObject[]): number => (
  timeRows.filter(timeRow => timeRow.trashStatus !== TrashStatus.TRASHED).length
)

export const countTrashedRows = (timeRows: TimeRow.AsObject[]): number => (
  timeRows.filter(timeRow => timeRow.trashStatus === TrashStatus.TRASHED).length
)

export const getRowsIdsToSelect = (
  allIdsList: string[],
  activeIdsList: string[],
  idToSelect: string,
): string[] => {
  const lastSelectedIf = activeIdsList[activeIdsList.length - 1]
  if (lastSelectedIf !== undefined) {
    const startIndex = allIdsList.indexOf(lastSelectedIf)
    const endIndex = allIdsList.indexOf(idToSelect)
    const isDownDirection = startIndex < endIndex
    return (
      isDownDirection
        ? allIdsList.slice(startIndex + 1, endIndex + 1)
        : allIdsList.slice(endIndex, startIndex)
    )
      .filter(id => !activeIdsList.includes(id))
  }
  return [idToSelect]
}

export const validateErroredTimeGroups = (
  timeGroups: TimeGroup.AsObject[],
  currentSite: Site.AsObject,
): {
  postErrorMessages: PostErrorMessages,
  clearPostErrorsList: string[],
} => {
  const postErrorMessages: PostErrorMessages = {}
  const clearPostErrorsList: string[] = []
  timeGroups.forEach(timeGroup => {
    const latestErrorMessage = errorMessageOfPostingTime(timeGroup, currentSite)
    if (latestErrorMessage === undefined && timeGroup.errorMessage !== latestErrorMessage) {
      clearPostErrorsList.push(timeGroup.id)
    } else {
      postErrorMessages[timeGroup.id] = latestErrorMessage
    }
  })
  return {
    postErrorMessages,
    clearPostErrorsList,
  }
}
